import React, { useCallback, useContext, useMemo } from 'react';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import TextField, { TextFieldProps } from '@material-ui/core/TextField';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import { KeyboardDateTimePicker, KeyboardDateTimePickerProps } from '@material-ui/pickers';

const useStyles = makeStyles(() =>
  createStyles({
    // The picker's own adornment sits directly after this one and carries an 8px left
    // margin; the negative right margin cancels it so the "X" sits beside the calendar
    // icon rather than a full icon-width away from it.
    clearAdornment: {
      marginRight: -14,
    },
    clearButton: {
      padding: 4,
    },
  }),
);

type ClearContextValue = {
  onClear: () => void;
  visible: boolean;
};

const ClearContext = React.createContext<ClearContextValue | null>(null);

/**
 * @material-ui/pickers v3 always overwrites `InputProps.endAdornment` with its own
 * calendar-icon adornment, so the supported way to render a second icon beside it is to
 * swap the picker's text field for one that re-adds our adornment around the picker's.
 *
 * Declared at module scope so its identity is stable — a `TextFieldComponent` that changed
 * between renders would remount the input and drop focus.
 */
function ClearableTextField(props: TextFieldProps) {
  const { InputProps, ...textFieldProps } = props;
  const classes = useStyles();
  const clear = useContext(ClearContext);

  return (
    <TextField
      {...textFieldProps}
      InputProps={{
        ...InputProps,
        endAdornment: (
          <>
            {clear?.visible ? (
              <InputAdornment position="end" className={classes.clearAdornment}>
                <IconButton
                  aria-label="clear date"
                  className={classes.clearButton}
                  onClick={clear.onClear}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null}
            {InputProps?.endAdornment}
          </>
        ),
      }}
    />
  );
}

/**
 * A `KeyboardDateTimePicker` with an "X" button next to the calendar icon that clears the
 * field. The button is hidden while the field is disabled or already empty.
 */
export default function ClearableKeyboardDateTimePicker(props: KeyboardDateTimePickerProps) {
  const { onChange, disabled, value } = props;

  const handleClear = useCallback(() => onChange(null, null), [onChange]);

  const hasValue = value !== null && value !== undefined && value !== '';

  const clearContextValue = useMemo(
    () => ({ onClear: handleClear, visible: !disabled && hasValue }),
    [handleClear, disabled, hasValue],
  );

  return (
    <ClearContext.Provider value={clearContextValue}>
      <KeyboardDateTimePicker {...props} TextFieldComponent={ClearableTextField} />
    </ClearContext.Provider>
  );
}
